import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { buildTheme } from '../design/theme';
import BottomNav from '../design/components/BottomNav';
import FabSheet from '../design/components/FabSheet';
import ToastHost from '../design/components/Toast';
import { getStoredTheme, syncLocalFollows } from './storage';
import '../design/animations.css';

// Persisted SWR cache provider — reads/writes to localStorage so navigations
// after a hard refresh feel "instant". Only persists JSON-safe data; large
// blobs are filtered out by the 64 KB per-entry cap.
const SWR_CACHE_KEY = 'tl_swr_cache_v1';
const MAX_ENTRY_BYTES = 64 * 1024;

function createPersistedCache() {
  let map = new Map();
  try {
    const raw = localStorage.getItem(SWR_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) map = new Map(parsed);
    }
  } catch { /* corrupt cache — start fresh */ }

  let saveScheduled = false;
  const scheduleSave = () => {
    if (saveScheduled) return;
    saveScheduled = true;
    // Coalesce writes; persist after 600 ms idle.
    setTimeout(() => {
      saveScheduled = false;
      try {
        const safe = [];
        for (const [k, v] of map.entries()) {
          // Skip entries that contain functions / errors / huge blobs.
          try {
            const s = JSON.stringify(v);
            if (s && s.length < MAX_ENTRY_BYTES) safe.push([k, v]);
          } catch { /* skip */ }
        }
        localStorage.setItem(SWR_CACHE_KEY, JSON.stringify(safe));
      } catch { /* quota or disabled storage — silently skip */ }
    }, 600);
  };

  return {
    get: (k) => map.get(k),
    set: (k, v) => { map.set(k, v); scheduleSave(); },
    delete: (k) => { map.delete(k); scheduleSave(); },
    keys: () => map.keys(),
  };
}

// V2-wide SWR defaults — gentle dedupe + revalidate on tab focus.
const SWR_DEFAULTS = {
  dedupingInterval: 60_000,
  revalidateOnFocus: true,
  shouldRetryOnError: false,
  errorRetryCount: 1,
  provider: createPersistedCache,
};

export default function V2Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [fabOpen, setFabOpen] = useState(false);
  const [themeName, setThemeName] = useState(() => getStoredTheme());
  const theme = buildTheme(themeName);
  const path = location.pathname;
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 1024 : false);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Listen for theme changes from any component
  useEffect(() => {
    const handler = (e) => setThemeName(e.detail?.name || getStoredTheme());
    window.addEventListener('tl-v2-theme-change', handler);
    window.addEventListener('storage', () => setThemeName(getStoredTheme()));
    return () => window.removeEventListener('tl-v2-theme-change', handler);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('taillink_token')) return;
    syncLocalFollows().catch(() => {});
  }, []);

  useEffect(() => {
    const resumeAfterLogin = () => {
      if (!localStorage.getItem('taillink_token')) return;
      const target = localStorage.getItem('tl_post_login_redirect');
      if (target && path === '/v2') {
        localStorage.removeItem('tl_post_login_redirect');
        navigate(target, { replace: true });
      }
    };
    resumeAfterLogin();
    const timer = setTimeout(resumeAfterLogin, 700);
    return () => clearTimeout(timer);
  }, [path, navigate]);

  useEffect(() => {
    setFabOpen(false);
  }, [path]);
  let active = 'home';
  if (path.startsWith('/v2/map')) active = 'map';
  else if (path.startsWith('/v2/community') || path.startsWith('/v2/challenges') || path.startsWith('/v2/blood') || path.startsWith('/v2/lost') || path.startsWith('/v2/events')) active = 'community';
  else if (path.startsWith('/v2/me') || path.startsWith('/v2/profile')) active = 'me';

  const handleNav = (id) => {
    if (id === 'home') navigate('/v2');
    else if (id === 'map') navigate('/v2/map');
    else if (id === 'community') navigate('/v2/community');
    else if (id === 'me') navigate('/v2/me');
  };

  const handleFabAction = (id) => {
    if (id === 'walk')   navigate('/v2/map');
    if (id === 'lost')   navigate('/v2/community');
    if (id === 'blood')  navigate('/v2/community');
    if (id === 'photo')  navigate('/v2/spot');
  };

  const handleFabOpen = () => {
    if (path.startsWith('/v2/me')) {
      setFabOpen(false);
      navigate('/v2/spot');
      return;
    }
    setFabOpen(true);
  };

  // Keep V2 navigation consistent across every route. Pages still keep their own
  // bottom padding, and the shared spacer below prevents fixed nav/FAB overlap.
  const hideNav = path.startsWith('/v2/admin') || path.startsWith('/v2/stamp-hunts');

  return (
    <SWRConfig value={SWR_DEFAULTS}>
    <div
      data-testid="v2-shell"
      className="tl-v2-root"
      style={{
        minHeight: '100vh', background: theme.bg, color: theme.text,
        fontFamily: "'Plus Jakarta Sans', 'IBM Plex Sans Thai', sans-serif",
        width: '100%', position: 'relative',
      }}
    >
      <Outlet context={{ theme, themeName, setThemeName }}/>
      {!hideNav && <div data-testid="v2-bottomnav-spacer" style={{ height: isDesktop && path.startsWith('/v2/map') ? 0 : 'calc(92px + env(safe-area-inset-bottom))' }} />}
      <ToastHost theme={theme}/>
      {!hideNav && (
        <>
          <BottomNav active={active} onNav={handleNav} theme={theme} onFab={handleFabOpen}/>
          {fabOpen && !path.startsWith('/v2/me') && <FabSheet theme={theme} onClose={() => setFabOpen(false)} onAction={handleFabAction}/>} 
        </>
      )}
    </div>
    </SWRConfig>
  );
}
